/**
 * 게시판 본문 **리치** sanitize — AxiEditor 로 쓴 글을 읽기·저장할 때 (2026-10-14).
 *
 * `lib/sanitize.ts` 의 `sanitizeBoardHtml`(9-23 · style 없음)의 상위집합이다. 서버
 * `sanitizeBoardBodyRich` 와 같은 것을 같은 방식으로 남긴다:
 *   - style 은 `allowed-styles.ts` 의 속성·값만
 *   - class 는 에디터 이름공간 `axe-*`(4.0) · `open-axe-*`(3.x 저장본) (+ code 의 `language-*`)만 — id 는 없다
 *   - data-* 는 적어 둔 이름만(`ALLOW_DATA_ATTR: false`), `data-file-id` 는 img 에만
 *   - iframe 은 YouTube embed 주소만, input 은 checkbox 만
 *   - svg 는 `svg`·`path`·`ellipse` 와 기하 속성만
 *
 * ⚠ DOMPurify 의 훅은 **전역**이다. 메일·채팅 sanitize 가 같은 인스턴스를 쓰므로, 훅은
 *   `active` 일 때만 일한다 — 이 함수 밖의 sanitize 결과는 한 글자도 안 바뀐다
 *   (`sanitize-board-rich.test.ts` 가 `sanitizeChatHtml` 로 대조한다).
 */

import DOMPurify from 'dompurify';
import { RICH_DATA_ATTRS, YT_EMBED, YT_ID, filterStyle } from './allowed-styles';

const TAGS = [
  'p', 'br', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'em', 'b', 'i', 'u', 's', 'strike', 'sub', 'sup',
  'a', 'ul', 'ol', 'li', 'blockquote', 'pre', 'code', 'hr', 'img',
  'table', 'thead', 'tbody', 'tr', 'th', 'td', 'colgroup', 'col',
  'figure', 'figcaption', 'video', 'iframe', 'input',
  'svg', 'path', 'ellipse',
];

const ATTRS = [
  'href', 'target', 'rel', 'title', 'src', 'alt', 'width', 'height',
  'border', 'colspan', 'rowspan', 'align', 'span',
  'class', 'style', 'aria-label', 'data-file-id', ...RICH_DATA_ATTRS,
  'controls', 'allow', 'allowfullscreen', 'frameborder', 'loading', 'referrerpolicy',
  'type', 'checked', 'disabled',
  // svg — DOMPurify 는 소문자 이름으로 견준다
  'viewbox', 'preserveaspectratio', 'd', 'cx', 'cy', 'rx', 'ry',
  'fill', 'fill-rule', 'fill-opacity', 'stroke', 'stroke-width', 'stroke-linejoin', 'stroke-linecap',
  'stroke-dasharray', 'stroke-opacity',
];

const ALIGN = new Set(['left', 'center', 'right', 'justify']);

let active = false;
let hooked = false;

function installHooks(): void {
  if (hooked) return;
  hooked = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (!active) return;
    const tag = node.nodeName.toLowerCase();
    const name = data.attrName;
    const value = data.attrValue ?? '';
    if (name === 'style') {
      const kept = filterStyle(value);
      if (kept) data.attrValue = kept; else data.keepAttr = false;
      return;
    }
    if (name === 'class') {
      const kept = value.split(/\s+/).filter((c) => /^(?:open-)?axe-[\w-]+$/.test(c) || (tag === 'code' && /^language-[\w-]+$/.test(c)));
      if (kept.length) data.attrValue = kept.join(' '); else data.keepAttr = false;
      return;
    }
    if (name === 'data-file-id' && tag !== 'img') { data.keepAttr = false; return; }
    if (name === 'data-youtube-id' && !YT_ID.test(value)) { data.keepAttr = false; return; }
    if (name === 'align' && !ALIGN.has(value.toLowerCase())) { data.keepAttr = false; return; }
    if (name === 'type' && !(tag === 'input' && value.toLowerCase() === 'checkbox')) { data.keepAttr = false; return; }
  });

  DOMPurify.addHook('afterSanitizeAttributes', (node) => {
    if (!active) return;
    const el = node as Element;
    const tag = el.nodeName.toLowerCase();
    if (tag === 'iframe' && !YT_EMBED.test(el.getAttribute('src') ?? '')) { el.remove(); return; }
    if (tag === 'input' && el.getAttribute('type') !== 'checkbox') { el.remove(); return; }
    if (tag === 'input') el.setAttribute('disabled', '');
  });
}

export interface SanitizeRichOptions {
  /**
   * 메일 「본문 이미지 처리」(2026-09-05 설정) — 그림을 아예 안 남긴다(2026-10-14 메일 읽기에 쓴다).
   * ★ 그린 뒤 감추지 않는다 — 감추면 이미 받아 온 뒤라 보낸 쪽이 「열었다」를 안다(`lib/sanitize.ts` 와 같은 까닭).
   */
  blockImages?: boolean;
}

export function sanitizeBoardHtmlRich(rawHtml: string, opts: SanitizeRichOptions = {}): string {
  if (typeof rawHtml !== 'string' || rawHtml.length === 0) return '';
  installHooks();
  active = true;
  try {
    return DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: opts.blockImages ? TAGS.filter((t) => t !== 'img') : TAGS,
      ALLOWED_ATTR: ATTRS,
      ALLOW_DATA_ATTR: false,
      ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i,
      FORBID_TAGS: ['script', 'style', 'object', 'embed', 'form', 'textarea', 'select', 'button', 'foreignobject', 'use', 'image'],
      FORBID_ATTR: ['id', 'srcdoc', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
    }) as unknown as string;
  } finally {
    active = false;
  }
}
